import apiRequest from './apiService';
import { Subscription } from '../interfaces/Subscription';
import { CREDITS_PER_CALL_MINUTE } from '../helpers/creditUsageDisplay';

/** Must match backend ``libs/global_variables.AVERAGE_CALL_MINUTES``. */
export const AVERAGE_CALL_MINUTES = 4;

export type UsageBillingMode =
  | 'included'
  | 'extra_credits'
  | 'allowance_exhausted'
  | 'unlimited';

export interface UsageResponse {
  minutes_used: number;
  minutes_limit: number | null;
  /** Legacy plans only: subscription minutes remaining */
  minutes_remaining?: number;
  messages_used: number;
  messages_limit: number | null;
  plan_tier?: string;
  plan_type?: string;
  /** Specific plan variant ID, e.g. call_starter_t1, bundle_business_t2 */
  plan_id?: string;
  enterprise?: boolean;
  monthly_credits?: number;
  /** Period cap from API ``credits_limit`` (company doc); null when uncapped enterprise. */
  monthly_credits_cap?: number;
  /** Credits consumed this period (bundle pool) */
  credits_used?: number;
  /** Same credit balance expressed as remaining call minutes if spent only on calls */
  equivalent_call_minutes_remaining?: number;
  /** Same credit balance expressed as remaining chat messages if spent only on chat */
  equivalent_chat_messages_remaining?: number;
  /** Non-enterprise bundle: allowance is one shared pool (minutes + messages convert to credits). */
  bundle_credit_pool?: boolean;
  /** From API ``credit_pool`` before normalization */
  credit_pool?: boolean;
  credits_remaining?: number;
  credits_limit?: number;
  usage_billing_mode?: UsageBillingMode;
  included_allowance_exhausted?: boolean;
  using_extra_credits?: boolean;
  extra_credits_billing_enabled?: boolean;
  overage_limit_reached?: boolean;
  /** Max metered overage credits (null = uncapped). */
  overage_credits_limit?: number | null;
  monthly_overage_credits_used?: number;
  overage_credits_remaining?: number | null;
  yearly_credits?: number;
  max_agents?: number | null;
  max_knowledges?: number | null;
  /**
   * Concurrent in-progress calls (usually from company doc; optional on get_usage).
   * Backend reserves ``(live_calls_number + 1) * AVERAGE_CALL_MINUTES``.
   */
  live_calls_number?: number;
}

export interface CompanyCallCreditsOptions {
  /** Concurrent live calls from the company doc (preferred over usage). */
  liveCallsNumber?: number | null;
}

type RawUsagePayload = UsageResponse & {
  credit_pool?: boolean;
  credits_remaining?: number;
  credits_limit?: number;
  credits_used?: number;
};

/** Map Pricing V2 ``get_usage`` fields to UI-friendly names. */
export function normalizeUsageResponse(raw: RawUsagePayload): UsageResponse {
  const bundlePool = raw.bundle_credit_pool ?? raw.credit_pool ?? false;
  const creditsCap = raw.monthly_credits_cap ?? raw.credits_limit;
  const creditsRemaining = raw.monthly_credits ?? raw.credits_remaining;

  return {
    ...raw,
    bundle_credit_pool: bundlePool,
    credit_pool: raw.credit_pool ?? bundlePool,
    monthly_credits_cap: creditsCap,
    monthly_credits: creditsRemaining,
    credits_remaining: raw.credits_remaining ?? creditsRemaining,
    credits_limit: raw.credits_limit ?? creditsCap,
  };
}

const fetchUsage = async (companyId: string): Promise<UsageResponse | null> => {
  if (!companyId) return null;
  try {
    const result = await apiRequest(
      'agent',
      'GET',
      `/subscription/get_usage?company_id=${encodeURIComponent(companyId)}`
    );
    const raw = (result as { message?: RawUsagePayload })?.message ?? null;
    if (!raw) return null;
    return normalizeUsageResponse(raw);
  } catch {
    return null;
  }
};

function resolvedLiveCallsNumber(
  usage: UsageResponse,
  options?: CompanyCallCreditsOptions
): number {
  const fromOptions = options?.liveCallsNumber;
  if (typeof fromOptions === 'number' && Number.isFinite(fromOptions)) {
    return Math.max(0, fromOptions);
  }
  const fromUsage = usage.live_calls_number;
  if (typeof fromUsage === 'number' && Number.isFinite(fromUsage)) {
    return Math.max(0, fromUsage);
  }
  return 0;
}

/** Included-pool credits left this period (not overage). */
function resolvedIncludedPoolCredits(usage: UsageResponse): number | null {
  const fromCredits = usage.monthly_credits ?? usage.credits_remaining;
  if (typeof fromCredits === 'number' && Number.isFinite(fromCredits)) {
    return Math.max(0, fromCredits);
  }
  const minutes = usage.equivalent_call_minutes_remaining;
  if (typeof minutes === 'number' && Number.isFinite(minutes)) {
    return Math.max(0, minutes * CREDITS_PER_CALL_MINUTE);
  }
  return null;
}

/**
 * Mirror backend ``company_can_use_metered_overage``: allow when uncapped, or when
 * remaining overage covers at least one call-minute of the reservation shortfall.
 */
function canCoverOverageShortfall(
  usage: UsageResponse,
  overageNeededCredits: number
): boolean {
  if (usage.extra_credits_billing_enabled !== true) return false;
  if (usage.overage_limit_reached === true) return false;
  const remaining = usage.overage_credits_remaining;
  if (remaining == null) return true;
  const required = Math.max(0, overageNeededCredits);
  const minimumRequired =
    required > 0 ? Math.min(required, CREDITS_PER_CALL_MINUTE) : 0;
  return remaining >= minimumRequired;
}

/**
 * Whether the company has capacity to bring an agent online, derived from the
 * (non-billing) get_usage endpoint. This mirrors backend ``can_process_call`` /
 * ``evaluate_call_process`` without requiring MANAGE_BILLING:
 *  - no usage (no plan configured / fetch failed) -> no capacity
 *  - uncapped pool (unlimited) with a configured plan -> capacity
 *  - included pool covers ``(live_calls + 1) * AVERAGE_CALL_MINUTES`` -> capacity
 *  - otherwise pay-as-you-go overage must cover at least one call-minute of the shortfall
 */
const companyHasCallCredits = (
  usage: UsageResponse | null,
  options?: CompanyCallCreditsOptions
): boolean => {
  if (!usage) return false;
  if (usage.usage_billing_mode === 'unlimited') return true;
  if (usage.overage_limit_reached === true) return false;

  const liveCalls = resolvedLiveCallsNumber(usage, options);
  const requiredCredits =
    (liveCalls + 1) * AVERAGE_CALL_MINUTES * CREDITS_PER_CALL_MINUTE;
  const poolRemaining = resolvedIncludedPoolCredits(usage);

  const onMeteredOveragePath =
    usage.using_extra_credits === true ||
    usage.usage_billing_mode === 'extra_credits' ||
    (usage.extra_credits_billing_enabled === true &&
      usage.included_allowance_exhausted === true);

  if (onMeteredOveragePath) {
    const pool = poolRemaining ?? 0;
    if (pool >= requiredCredits) return true;
    const overageNeeded = Math.max(0, requiredCredits - pool);
    return canCoverOverageShortfall(usage, overageNeeded);
  }

  if (poolRemaining != null) {
    if (poolRemaining >= requiredCredits) return true;
    // Last included minutes may still start a call via pay-as-you-go.
    if (usage.extra_credits_billing_enabled === true) {
      const overageNeeded = Math.max(0, requiredCredits - poolRemaining);
      return canCoverOverageShortfall(usage, overageNeeded);
    }
    return false;
  }

  const remainingMinutes = usage.equivalent_call_minutes_remaining;
  if (typeof remainingMinutes === 'number') return remainingMinutes > 0;
  return true;
};

const fetchStripeSubscription = async (
  billingCustomerId: string
): Promise<Subscription | undefined> => {
  if (!billingCustomerId) return;

  try {
    const result = await apiRequest(
      'agent',
      'GET',
      `/subscription/get_subscription?billing_customer_id=${billingCustomerId}`
    );

    const subscriptions = result?.message?.subscriptions;
    const activeSubscription = subscriptions?.find(
      (subscription: Subscription) =>
        subscription.status === 'active' ||
        subscription.status === 'trialing' ||
        subscription.status === 'past_due'
    );
    if (activeSubscription) return activeSubscription;

    const canceledSubscriptions = (subscriptions ?? []).filter(
      (subscription: Subscription) => subscription.status === 'canceled'
    );
    if (canceledSubscriptions.length > 0) {
      return canceledSubscriptions.sort(
        (a: Subscription, b: Subscription) =>
          (b.current_period_end ?? 0) - (a.current_period_end ?? 0)
      )[0];
    }

    return undefined;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const changeSubscriptionPlan = async (
  userId: string,
  planId: string,
  companyId?: string
): Promise<unknown> => {
  const payload = {
    user_id: userId,
    plan_id: planId,
    ...(companyId ? { company_id: companyId } : {}),
  };

  const attempts: Array<{ base: 'agent' | 'admin'; path: string }> = [
    { base: 'agent', path: '/subscription/change_subscription_plan' },
    { base: 'agent', path: '/subscription/change_plan' },
    { base: 'agent', path: '/subscription/update_subscription_plan' },
    { base: 'admin', path: '/subscription/change_subscription_plan' },
  ];

  let lastError: unknown = null;

  for (const attempt of attempts) {
    try {
      return await apiRequest(attempt.base, 'POST', attempt.path, payload);
    } catch (error: unknown) {
      lastError = error;
      const message = error instanceof Error ? error.message : '';
      const notFound =
        message.toLowerCase().includes('resource not found') ||
        message.includes('404');
      if (!notFound) {
        throw error;
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Resource not found');
};

export {
  changeSubscriptionPlan,
  companyHasCallCredits,
  fetchStripeSubscription,
  fetchUsage,
};
